import { PARK_GROUP_NAME } from 'common/constants';
import { Logger } from 'common/logger';
import { last } from 'lodash';

export function getAllowedParks({ parkList = [], allowedScreens = {} }) {
    const allowedParkIds = Object.keys(allowedScreens);

    const filteredParks = parkList.filter(
        (p) => p.group === PARK_GROUP_NAME && allowedParkIds.indexOf(p.key.toLowerCase()) !== -1
    );

    return filteredParks;
}

// Normalize the turbine name to remove leading zeros D03R -> D3R
// And remove prefix before "-" GN-D7M -> D7M
export const normalizeTurbineNameSystematic = (parkName, destinationName) => {
    if (parkName === 'gym') {
        // remove leading zeros
        // D03R -> D3R

        return destinationName.replace(/0+(\d)/g, '$1');
    }

    if (parkName === 'galloper') {
        // remove prefix before "-"
        // GN-D7M -> D7M

        const parts = destinationName.split('-');

        return last(parts);
    }

    return destinationName;
};

export const hasDestination = (parkName, parkVessels = [], name) => {
    const destinations = parkVessels.flatMap((p) => p.destinations?.value || []);

    if (!name) {
        return false;
    }

    const normalizedName = normalizeTurbineNameSystematic(parkName, name);

    return destinations.some((d) => normalizeTurbineNameSystematic(parkName, d.destinationName) === normalizedName);
};

export const getTurbineOrderPriority = (parkName, parkVessels = []) => (turbineData) => {
    const isInManifest = hasDestination(parkName, parkVessels, turbineData.name);
    const hasRestriction = !!turbineData.restrictions?.value;
    const isOccupied = (turbineData.person_count?.value ?? 0) > 0;
    const isInTransfer = (turbineData.is_released?.value ?? 0) > 0;
    const isUnavailable = !!turbineData.is_marked_unavailable?.value;
    const isInAlarm = turbineData.state?.value === 'ALARM_STOPPED' || turbineData.state?.value === 'ALARM_RUNNING';

    /**
     * Ordering requirement:
     * 1. Restriction
     * 2. Manifest
     * 3. Occupied (only if not in Manifest)
     * 4. In Transfer
     * 5. Unavailable
     * 6. Alarm
     *
     * Important:
     * Buckets are resolved by priority order.
     * If a turbine matches multiple states, the first matching state wins.
     */
    if (hasRestriction) {
        return 1;
    }

    if (isInManifest) {
        return 2;
    }

    if (isOccupied) {
        return 3;
    }

    if (isInTransfer) {
        return 4;
    }

    if (isUnavailable) {
        return 5;
    }

    if (isInAlarm) {
        return 6;
    }

    return 999;
};

export const sortByDestination = (parkName, parkVessels = []) => (a, b) => {
    const getPriority = getTurbineOrderPriority(parkName, parkVessels);

    const aPriority = getPriority(a);
    const bPriority = getPriority(b);

    if (aPriority !== bPriority) {
        return aPriority - bPriority;
    }

    return a.name.localeCompare(b.name);
};
export const turbineHasAnyActivities = (parkName, parkVessels = []) => (turbineData) => {
    const { name, person_count, restrictions, is_released, state, is_marked_unavailable } = turbineData;

    /*
        Turbines with Restriction (Blue triangle)
        Turbines in Manifest
        Turbines occupied but not in Manifest
        Turbines in Transfer
        Turbines Unavailable
        Turbines in Alarm state
    */
    if (hasDestination(parkName, parkVessels, name)) {
        Logger.debug(`Turbine ${name} has destination`, parkVessels);
        return true;
    }

    if (restrictions?.value) {
        return true;
    }

    if (person_count?.value) {
        return true;
    }

    if (is_released.value > 0) {
        return true;
    }
    if (is_marked_unavailable?.value) {
        return true;
    }
    if (state?.value === 'ALARM_STOPPED') {
        return true;
    }
    return false;
};

export function getDirectionFromDegrees(degree) {
    if (!degree) {
        return '';
    }

    const directions = [
        'N',
        'NNE',
        'NE',
        'ENE',
        'E',
        'ESE',
        'SE',
        'SSE',
        'S',
        'SSW',
        'SW',
        'WSW',
        'W',
        'WNW',
        'NW',
        'NNW',
    ];
    const index = Math.round(degree / 22.5) % 16;
    return directions[index];
}

export const waveHeightThresholdDefault = 2;

export function getWaveRiskLevel(value) {
    if (value >= 1.5) {
        return 'RED';
    }

    if (value >= 1.1) {
        return 'AMBER';
    }

    return 'GREEN';
}

export function getWavePercent(value, maxWaveHeight) {
    if (value > maxWaveHeight) {
        return '100%';
    }

    return `${(value / maxWaveHeight) * 100}%`;
}
